import { z } from 'zod';
/**
 * BUSINESS SEGMENT CONDITIONS SYSTEM
 * ===================================
 *
 * This file defines the types for business segment conditions - the rules that determine
 * which users belong to a segment. The most confusing part is the relative date system.
 *
 * RELATIVE DATE SYSTEM EXPLAINED:
 * ===============================
 *
 * Relative dates are calculated from "today" and can go backwards (BEFORE) or forwards (AFTER).
 *
 * BASIC CONCEPTS:
 * - BEFORE = "X days before today" = today - X days (past date)
 * - AFTER = "X days after today" = today + X days (future date)
 *
 * OPERATOR COMBINATIONS:
 * ======================
 *
 * 1. LESS_THAN + BEFORE = "date < (today - X days)" = "date is older than X days ago"
 *    Example: Find users who booked more than 180 days ago
 *    { value: 180, unit: 'days', direction: 'before', operator: 'less_than' }
 *
 * 2. GREATER_THAN + BEFORE = "date > (today - X days)" = "date is more recent than X days ago"
 *    Example: Find users who booked within the last 30 days
 *    { value: 30, unit: 'days', direction: 'before', operator: 'greater_than' }
 *
 * 3. LESS_THAN + AFTER = "date < (today + X days)" = "date is before X days from now"
 *    Example: Find users with bookings before next month
 *    { value: 30, unit: 'days', direction: 'after', operator: 'less_than' }
 *
 * 4. GREATER_THAN + AFTER = "date > (today + X days)" = "date is after X days from now"
 *    Example: Find users with future bookings more than 7 days away
 *    { value: 7, unit: 'days', direction: 'after', operator: 'greater_than' }
 *
 * COMMON BUSINESS USE CASES:
 * ==========================
 *
 * 1. BOARDING CROSS-SELL: "Users who had daycare more recently than 180 days ago but never booked boarding"
 *    - Daycare date: GREATER_THAN + BEFORE (date > today - 180 days)
 *    - Boarding bookings: EQUALS 0
 *
 * 2. RECENT CUSTOMERS: "Users who booked within the last 30 days"
 *    - Booking date: GREATER_THAN + BEFORE (date > today - 30 days)
 *
 * 3. FUTURE BOOKINGS: "Users with bookings more than 7 days in the future"
 *    - Booking date: GREATER_THAN + AFTER (date > today + 7 days)
 *
 * 4. OLD CUSTOMERS: "Users who haven't booked in over 90 days"
 *    - Last booking date: LESS_THAN + BEFORE (date < today - 90 days)
 *
 * DEBUGGING TIPS:
 * ===============
 *
 * 1. Always think: "What date am I comparing against?"
 * 2. BEFORE = past reference point, AFTER = future reference point
 * 3. LESS_THAN = "older than", GREATER_THAN = "more recent than"
 * 4. Test with concrete dates: "If today is Jan 1, 2024, what does 180 days BEFORE mean?"
 * 5. Remember: GREATER_THAN + BEFORE = "within the last X days", not "less than X days ago"
 *
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts
 */
/**
 * Units for relative date calculations
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts
 */
export var DateUnit;
(function (DateUnit) {
    DateUnit["DAYS"] = "days";
    // Fow now we only support days
})(DateUnit || (DateUnit = {}));
/**
 * Direction for relative date calculations
 *
 * ⚠️ CONFUSING LOGIC WARNING ⚠️
 *
 * The direction determines the reference point for the date calculation:
 *
 * BEFORE = "X days before today" = today - X days (past date)
 * AFTER = "X days after today" = today + X days (future date)
 *
 * Examples:
 * - { value: 30, unit: 'days', direction: 'before' } = 30 days ago
 * - { value: 30, unit: 'days', direction: 'after' } = 30 days from now
 *
 * When combined with operators:
 * - LESS_THAN + BEFORE = "date < (today - X days)" = "date is older than X days ago"
 * - GREATER_THAN + BEFORE = "date > (today - X days)" = "date is newer than X days ago"
 * - LESS_THAN + AFTER = "date < (today + X days)" = "date is before X days from now"
 * - GREATER_THAN + AFTER = "date > (today + X days)" = "date is after X days from now"
 *
 * Common use cases:
 * - "Users who booked more than 180 days ago": LESS_THAN + BEFORE
 * - "Users who booked less than 30 days ago": GREATER_THAN + BEFORE
 * - "Users with future bookings": GREATER_THAN + AFTER
 * - "Users with bookings before next month": LESS_THAN + AFTER
 */
export var RelativeDateDirection;
(function (RelativeDateDirection) {
    RelativeDateDirection["BEFORE"] = "before";
    RelativeDateDirection["AFTER"] = "after";
})(RelativeDateDirection || (RelativeDateDirection = {}));
/**
 * All reach built in potential fields for a condition in a segment
 */
export const ConditionFieldEnum = z.enum(['userId', 'email', 'phone', 'firstName', 'lastName']);
export const sqlSegmentConditionSchema = z.object({
    sql_segment_id: z.string(),
});
/**
 * All potential operators for a condition in a segment
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts
 */
export var ConditionOperatorEnumType;
(function (ConditionOperatorEnumType) {
    ConditionOperatorEnumType["EQUALS"] = "equals";
    ConditionOperatorEnumType["NOT_EQUALS"] = "not_equals";
    ConditionOperatorEnumType["GREATER_THAN"] = "greater_than";
    ConditionOperatorEnumType["LESS_THAN"] = "less_than";
    ConditionOperatorEnumType["EXISTS"] = "exists";
    ConditionOperatorEnumType["NOT_EXISTS"] = "not_exists";
    /**
     * These operators are specific for date fields
     */
    ConditionOperatorEnumType["EQUALS_MONTH_DAY"] = "equals_month_day";
    ConditionOperatorEnumType["EQUALS_MONTH_DAY_YEAR"] = "equals_month_day_year";
    ConditionOperatorEnumType["BETWEEN_MONTH_DAY"] = "between_month_day";
    ConditionOperatorEnumType["BETWEEN_MONTH_DAY_YEAR"] = "between_month_day_year";
    /**
     * These operators are specific for array fields
     */
    ConditionOperatorEnumType["ARRAY_CONTAINS"] = "array_contains";
    /**
     * String pattern matching (substring search)
     */
    ConditionOperatorEnumType["CONTAINS"] = "contains";
    ConditionOperatorEnumType["NOT_CONTAINS"] = "not_contains";
    /**
     * IN: matches if the field value strictly equals one of the values in the array.
     * LHS can be any type (string, number, etc.); RHS must be an array of that same type.
     */
    ConditionOperatorEnumType["IN"] = "in";
})(ConditionOperatorEnumType || (ConditionOperatorEnumType = {}));
export const ConditionOperatorEnum = z.nativeEnum(ConditionOperatorEnumType);
/**
 * Structure for relative date values
 *
 * This creates a relative date calculation based on today's date.
 *
 * @example
 * // 30 days ago
 * { type: 'relative', value: 30, unit: 'days', direction: 'before' }
 *
 * @example
 * // 7 days from now
 * { type: 'relative', value: 7, unit: 'days', direction: 'after' }
 *
 * @example
 * // For boarding cross-sell: users who had daycare more than 180 days ago
 * {
 *   field: 'Daycare Invoice Period Start Date',
 *   value: [{ type: 'relative', value: 180, unit: 'days', direction: 'before' }],
 *   operator: 'less_than'  // date < (today - 180 days)
 * }
 *
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts
 */
export const relativeDateSchema = z.object({
    type: z.literal('relative'),
    value: z.number().describe('Number of days to add/subtract from today'),
    unit: z.nativeEnum(DateUnit),
    direction: z.nativeEnum(RelativeDateDirection),
});
/**
 * Structure for between date values
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts
 */
export const betweenDateSchema = z.object({
    startDate: z.union([z.coerce.date(), relativeDateSchema]),
    endDate: z.union([z.coerce.date(), relativeDateSchema]),
});
/**
 * A single condition in a segment.
 * In condition groups, every item has a type for a discriminated union; field conditions use type: 'property'.
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts
 */
const fieldConditionSchema = z.object({
    type: z.literal('property').optional(),
    field: z.union([ConditionFieldEnum, z.string()]),
    value: z
        .array(z.union([z.string(), z.number(), z.boolean(), z.date(), relativeDateSchema, betweenDateSchema]))
        .describe('The value to compare the field against (string, number, boolean, date)'),
    operator: ConditionOperatorEnum,
});
/**
 * Operators for count-based quantifier comparisons.
 * Used to specify how to compare the count of matching related resources.
 *
 * Common patterns:
 * - "has any": countOperator: 'gte', countValue: 1
 * - "does not have": countOperator: 'eq', countValue: 0
 * - "has at least 3": countOperator: 'gte', countValue: 3
 * - "has exactly 2": countOperator: 'eq', countValue: 2
 */
export var CountOperator;
(function (CountOperator) {
    CountOperator["EQ"] = "eq";
    CountOperator["GT"] = "gt";
    CountOperator["GTE"] = "gte";
    CountOperator["LT"] = "lt";
    CountOperator["LTE"] = "lte";
})(CountOperator || (CountOperator = {}));
/**
 * Describes which related resource to query and how it links to the contact.
 * Mirrors the "field" concept for the primary entity.
 */
export const relatedResourceSchema = z.object({
    /**
     * The schema definition ID of the related resource to query
     * (e.g., the PolicyLift policy schema ID)
     */
    schemaId: z.string(),
    /**
     * JSON path in the related resource's data that links to the contact's external_id
     * (e.g., "customerId" for policies that have a customerId field)
     */
    foreignKeyPath: z.string(),
});
/**
 * Related-resource condition: query by whether the user has matching related resources.
 * Uses nested relatedResource: { schemaId, foreignKeyPath } to group the "which resource" fields.
 *
 * Common patterns:
 * - "has any matching": countOperator: 'gte', countValue: 1
 * - "does not have any": countOperator: 'eq', countValue: 0
 * - "has at least 3": countOperator: 'gte', countValue: 3
 * - "has exactly 2": countOperator: 'eq', countValue: 2
 */
export const relatedResourceConditionSchema = z.object({
    type: z.literal('related_resource'),
    /**
     * Which related resource to query and how it links to the contact
     */
    relatedResource: relatedResourceSchema,
    /**
     * How to compare the count of matching resources (eq, gt, gte, lt, lte)
     */
    countOperator: z.nativeEnum(CountOperator),
    /**
     * The count value to compare against (e.g., 0 for "none", 1 for "at least one")
     */
    countValue: z.number().int().min(0),
    /**
     * Conditions to filter the related resources (applied as AND logic)
     * These conditions are evaluated against the related resource's data
     */
    conditions: z.array(fieldConditionSchema),
});
export function isRelatedResourceCondition(condition) {
    return (typeof condition === 'object' &&
        condition !== null &&
        'type' in condition &&
        condition.type === 'related_resource');
}
/**
 * Type guard for field (property) conditions.
 * Supports discriminated shape (type === 'property') and legacy shape ('field' in condition).
 */
export function isFieldCondition(condition) {
    if (typeof condition !== 'object' || condition === null)
        return false;
    const c = condition;
    if (c.type === 'property')
        return true;
    if (c.type === 'related_resource' || c.type === 'group')
        return false;
    return 'field' in c;
}
export function isSqlSegmentCondition(condition) {
    return 'sql_segment_id' in condition;
}
/**
 * Logic type for condition groups
 */
export var ConditionGroupLogic;
(function (ConditionGroupLogic) {
    ConditionGroupLogic["AND"] = "and";
    ConditionGroupLogic["OR"] = "or";
})(ConditionGroupLogic || (ConditionGroupLogic = {}));
/**
 * Base condition item schema (field or related resource, no groups).
 * Used to build both runtime and doc-generation schemas.
 */
const baseConditionItemSchema = z.union([fieldConditionSchema, relatedResourceConditionSchema]);
/**
 * Flattened condition group schema for OpenAPI doc generation.
 * OpenAPI generators can't handle z.lazy() recursive schemas, so we provide
 * a 2-level deep version for documentation. This covers practical use cases.
 */
const level2GroupSchema = z
    .object({
    type: z.literal('group').optional().default('group'),
    logic: z.nativeEnum(ConditionGroupLogic),
    conditions: z.array(baseConditionItemSchema),
})
    .transform(val => ({ ...val, type: 'group' }));
const level1GroupSchema = z
    .object({
    type: z.literal('group').optional().default('group'),
    logic: z.nativeEnum(ConditionGroupLogic),
    conditions: z.array(z.union([baseConditionItemSchema, level2GroupSchema])),
})
    .transform(val => ({ ...val, type: 'group' }));
const conditionGroupSchemaFlat = z
    .object({
    type: z.literal('group').optional().default('group'),
    logic: z.nativeEnum(ConditionGroupLogic),
    conditions: z.array(z.union([baseConditionItemSchema, level1GroupSchema])),
})
    .transform(val => ({
    ...val,
    type: 'group',
}));
/**
 * Recursive condition group schema for runtime validation.
 * Uses z.lazy() to support unlimited nesting depth.
 */
const conditionGroupSchemaRecursive = z.lazy(() => z
    .object({
    type: z.literal('group').optional().default('group'),
    logic: z.nativeEnum(ConditionGroupLogic),
    conditions: z.array(z.union([fieldConditionSchema, relatedResourceConditionSchema, conditionGroupSchemaRecursive])),
})
    .transform(val => ({ ...val, type: 'group' })));
/**
 * Zod schema for condition group.
 * - At runtime: uses recursive z.lazy() for unlimited nesting depth
 * - During doc generation: uses flattened schema (2-level depth) for OpenAPI compatibility
 */
export const conditionGroupSchema = typeof process !== 'undefined' && process.env?.GENERATE_DOCS === 'true'
    ? conditionGroupSchemaFlat
    : conditionGroupSchemaRecursive;
/**
 * Type guard for condition groups.
 * Supports discriminated shape (type === 'group') and legacy shape (logic + conditions).
 */
export function isConditionGroup(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    if (v.type === 'group')
        return true;
    return 'logic' in v && 'conditions' in v && Array.isArray(v.conditions);
}
/**
 * The union of all managed fields (@see ConditionFieldEnum) + custom fields (string)
 */
export const ConditionSchema = z.union([
    fieldConditionSchema,
    sqlSegmentConditionSchema,
    relatedResourceConditionSchema,
]);
// Schema for arrays of field conditions specifically (no SQL segments)
export const fieldConditionsArraySchema = z.array(fieldConditionSchema);
/**
 * Segment condition groups format: array of groups (AND between groups).
 * Supports related-resource conditions and grouped AND/OR logic.
 */
export const segmentConditionGroupsSchema = z.array(conditionGroupSchema);
export const BusinessSegmentConditionsSchema = z.array(ConditionSchema);
//# sourceMappingURL=conditions.js.map